import React, { useMemo } from 'react';
import type { ImageStyle, ViewStyle } from 'react-native';
import { TouchableOpacity, StyleSheet, Image, Text, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { ApiEvent } from 'src/core/models/api/ApiEvent';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

interface ArchiveItemProps {
	data: ApiEvent;
}

function ArchiveItem({ data }: ArchiveItemProps) {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const contentWidth = useContentWidth();
	const image = data.image ? data.image : 'https://via.placeholder.com/300x300';

	const sizedStyles = useMemo((): { item: ViewStyle; img: ImageStyle } => {
		const tileSize = Platform.OS === 'android' ? (contentWidth - 107) / 4 : (contentWidth - 135) / 4;

		return {
			item: {
				width: tileSize,
				marginRight: 12.5,
				marginLeft: 12.5,
				marginBottom: Dimens.margin10,
				alignItems: 'center',
			},
			img: {
				width: tileSize,
				height: tileSize,
				borderRadius: 10,
			},
		};
	}, [contentWidth]);

	const openEvent = async () => {
		if (data.theme?.data) {
			useEventStore.getState().setTheme(data.theme.data);
		}
		useAnalyticsStore.getState().eventOpened(data.uuid);
		useAnalyticsStore.getState().eventTime(data.uuid, 'start');
		useNavigationStore.getState().setActiveEventByCode(data.code);
		useNavigationStore.getState().setActiveEventByUuid(data.uuid);

		await useEventStore.getState().fetchEvent(data.uuid);

		const updatedEvent = useEventStore.getState().eventData.find((e) => e.uuid === data.uuid) ?? data;

		useNavigationStore.getState().navigate(Constants.screenEvent, {
			title: updatedEvent.name,
			eventUuid: updatedEvent.uuid,
		});
		navigation.navigate(Constants.screenEvent, {
			title: updatedEvent.name,
			eventLogo: updatedEvent.logo,
			eventUuid: updatedEvent.uuid,
		});
	};

	return (
		<TouchableOpacity style={sizedStyles.item} onPress={openEvent}>
			<Image resizeMode="cover" source={{ uri: image }} style={sizedStyles.img} />
			<Text numberOfLines={2} style={styles.text}>
				{data.name}
			</Text>
		</TouchableOpacity>
	);
}

export default ArchiveItem;

const styles = StyleSheet.create({
	text: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize11,
		color: Colors.greyTextDark,
		marginTop: Dimens.margin5,
		lineHeight: 15,
		height: 35,
		textAlign: 'center',
	},
});
